import { cn } from "@/lib/utils";
import { AnimatedNumber } from "./AnimatedNumber";
import { clamp } from "./dial-math";

interface EnergyGaugeProps {
  /** Current value, e.g. watts. */
  value: number;
  /** Gauge full-scale, e.g. expected max household draw. */
  max: number;
  label?: string;
  unit?: string;
  className?: string;
}

const SIZE = 160;
const STROKE = 12;
const R = (SIZE - STROKE) / 2;

export function EnergyGauge({
  value,
  max,
  label = "Power",
  unit = "W",
  className,
}: EnergyGaugeProps) {
  const ratio = clamp(max === 0 ? 0 : value / max, 0, 1);
  return (
    <div
      data-slot="energy-gauge"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cn("relative inline-block", className)}
    >
      <svg
        width={SIZE}
        height={SIZE / 2 + STROKE}
        viewBox={`0 0 ${SIZE} ${SIZE / 2 + STROKE}`}
        aria-hidden="true"
        focusable="false"
      >
        <g transform={`rotate(180 ${SIZE / 2} ${SIZE / 2})`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray="0.5 1"
            className="stroke-muted"
          />
          {/* Round linecap draws a dot at zero length; skip the fill arc entirely. */}
          {ratio > 0.001 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              strokeWidth={STROKE}
              strokeLinecap="round"
              pathLength={1}
              strokeDasharray={`${0.5 * ratio} 1`}
              className="stroke-domain-energy transition-[stroke-dasharray] duration-300 ease-signature"
            />
          )}
        </g>
      </svg>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
        <span className="text-2xl font-semibold tabular-nums">
          <AnimatedNumber value={value} />
          <span className="ml-1 text-sm font-normal text-muted-foreground">{unit}</span>
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}
